import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReportMetadata } from '../../types';
import { useRememberedState } from '../../hooks/useRemembered';
import {
    Camera, ChevronDown, ChevronUp, Download, ImagePlus, Loader2, Trash2, AlertCircle, CheckCircle2, Megaphone, Star, RefreshCw,
} from 'lucide-react';
import {
    listProjectMedia,
    uploadProjectMedia,
    deleteProjectMedia,
    setProjectMediaTitelbild,
    updateProjectMediaCaption,
    MEDIA_TABLE_MISSING_TEXT,
} from '../../services/projectMediaService';
import { downloadProjectReferenceImage } from '../../services/projectReferenceImage';
import {
    ProjectMedia,
    describeFileSize,
    resolutionVerdict,
    resolutionVerdictText,
    REFERENCE_IMAGE_MAX_BYTES,
    CAPTION_MAX,
} from '../../services/projectReferenceLogic';

interface Props {
    metadata: ReportMetadata;
    report: any;
    projectId: string | undefined;
    onUpdate: (report: any) => void;
    isReadOnly?: boolean;
    memoryKey: string;
    /** Hochzaehlen, wenn ausserhalb der Karte ein Bild dazukam (Datenpflege-Dialog) — die Galerie laedt dann neu. */
    refreshToken?: number;
}

const DESCRIPTION_MAX = 2000;

/**
 * Karte „Projektbeschreibung & Projektbilder" im Projekt-Hub.
 *
 * Anlass (Betreiber 12.09.2026): mehr Werbung in sozialen Medien. Die
 * BESCHREIBUNG liegt im Projekt-JSON (metadata.projectDescription, Muster
 * ProjectMasterData: lokaler Text, ein onUpdate je Blur). Die BILDER liegen
 * in der Tabelle `project_media` (Nr. 99) und werden hier direkt gelesen und
 * geschrieben — unabhaengig vom Projekt-Save. Beides erscheint auf dem
 * Referenzblatt (Kataloge & Vorlagen → Referenzblaetter) und unter
 * Kataloge & Vorlagen → Projektbilder.
 */
export const ProjectReferenceCard: React.FC<Props> = ({ metadata, report, projectId, onUpdate, isReadOnly = false, memoryKey, refreshToken = 0 }) => {
    const [isExpanded, setIsExpanded] = useRememberedState(`${memoryKey}.referenz`, false);
    const [localDescription, setLocalDescription] = useState(metadata.projectDescription || '');
    const [items, setItems] = useState<ProjectMedia[]>([]);
    const [loading, setLoading] = useState(false);
    const [tabelleFehlt, setTabelleFehlt] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [captionDrafts, setCaptionDrafts] = useState<Record<string, string>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Von aussen (Sync, anderes Geraet, eigener Blur) geaenderten Text
    // uebernehmen. Waehrend des Tippens aendert sich metadata nicht — der
    // Commit passiert erst beim Verlassen des Feldes.
    useEffect(() => {
        setLocalDescription(metadata.projectDescription || '');
    }, [metadata.projectDescription]);

    const reload = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const res = await listProjectMedia(projectId);
            setItems(res.items);
            setTabelleFehlt(res.tabelleFehlt);
            setCaptionDrafts(Object.fromEntries(res.items.map(i => [i.id, i.caption || ''])));
        } catch (e: any) {
            setError(e?.message || 'Bilder konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { void reload(); }, [reload, refreshToken]);

    const commitDescription = () => {
        const next = localDescription.trim().slice(0, DESCRIPTION_MAX);
        if (next === (metadata.projectDescription || '')) return;
        onUpdate({ ...report, metadata: { ...metadata, projectDescription: next || undefined } });
    };

    const handleFiles = async (files: FileList | File[] | null | undefined) => {
        const list = files ? Array.from(files) : [];
        if (list.length === 0 || isReadOnly || tabelleFehlt) return;
        setError(null);
        setNotice(null);
        setBusy('upload');
        let current = items;
        let ok = 0;
        const fehler: string[] = [];
        try {
            for (let i = 0; i < list.length; i++) {
                setUploadProgress({ done: i, total: list.length });
                try {
                    const neu = await uploadProjectMedia(list[i], projectId || '', current);
                    current = [...current, neu];
                    ok++;
                } catch (e: any) {
                    fehler.push(`${list[i].name}: ${e?.message || 'Upload fehlgeschlagen'}`);
                }
            }
        } finally {
            setUploadProgress(null);
            setBusy(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
        await reload();
        if (ok > 0) setNotice(ok === 1 ? 'Bild hochgeladen.' : `${ok} Bilder hochgeladen.`);
        if (fehler.length > 0) setError(fehler.join('\n'));
    };

    const handleDelete = async (item: ProjectMedia) => {
        if (isReadOnly) return;
        if (!window.confirm(`„${item.fileName}" wirklich löschen? Die Datei wird aus dem Speicher entfernt.`)) return;
        setError(null);
        setBusy(item.id);
        try {
            await deleteProjectMedia(item);
            await reload();
            setNotice('Bild gelöscht.');
        } catch (e: any) {
            setError(e?.message || 'Löschen fehlgeschlagen.');
        } finally {
            setBusy(null);
        }
    };

    const handleTitelbild = async (item: ProjectMedia) => {
        if (isReadOnly || item.titelbild) return;
        setError(null);
        setBusy(item.id);
        try {
            await setProjectMediaTitelbild(item.projectId, item.id);
            await reload();
        } catch (e: any) {
            setError(e?.message || 'Titelbild konnte nicht gesetzt werden.');
        } finally {
            setBusy(null);
        }
    };

    const handleCaptionBlur = async (item: ProjectMedia) => {
        if (isReadOnly) return;
        const draft = (captionDrafts[item.id] ?? '').trim();
        if (draft === (item.caption || '')) return;
        try {
            await updateProjectMediaCaption(item.id, draft);
            setItems(prev => prev.map(i => (i.id === item.id ? { ...i, caption: draft || null } : i)));
        } catch (e: any) {
            setError(e?.message || 'Bildunterschrift nicht gespeichert.');
        }
    };

    const handleDownload = async (item: ProjectMedia, index: number) => {
        setError(null);
        setBusy(item.id);
        try {
            await downloadProjectReferenceImage(item, metadata.projectNumber, metadata.projectName, items.length > 1 ? index + 1 : undefined);
        } catch (e: any) {
            setError(e?.message || 'Download fehlgeschlagen.');
        } finally {
            setBusy(null);
        }
    };

    const canUpload = !isReadOnly && !tabelleFehlt && !!projectId && busy === null;
    const hasDescription = !!(metadata.projectDescription || '').trim();

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <button onClick={() => setIsExpanded(!isExpanded)} className="w-full text-left px-6 py-4 flex items-center justify-between bg-slate-50/50 hover:bg-slate-100/50">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isExpanded ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`}><Camera className="w-5 h-5" /></div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">Projektbeschreibung & Projektbilder</h2>
                        {!isExpanded && (
                            <p className="text-xs text-slate-500">
                                {items.length === 0 && !hasDescription
                                    ? 'Für Referenzblatt und Social Media — noch nichts hinterlegt'
                                    : `${items.length === 1 ? '1 Bild' : `${items.length} Bilder`} · ${hasDescription ? 'Beschreibung vorhanden' : 'Keine Beschreibung'}`}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {!isExpanded && items.slice(0, 3).map(i => (
                        <img key={i.id} src={i.url} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-200 hidden sm:block" loading="lazy" />
                    ))}
                    <div className={`p-2 rounded-full ${isExpanded ? 'bg-slate-100 text-slate-600' : 'text-slate-400'}`}>{isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</div>
                </div>
            </button>

            {isExpanded && (
                <div className="p-6 bg-white space-y-6">
                    <div className="flex items-start gap-2 text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-2">
                        <Megaphone className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Beschreibung und Bilder erscheinen auf dem Referenzblatt und unter Kataloge & Vorlagen → „Projektbilder" — von dort holt sich der Innendienst das Material für Social Media. Bitte Originale in voller Auflösung hochladen, nicht den WhatsApp-Export. Das Bild mit dem Stern ist das Titelbild fürs Referenzblatt.</span>
                    </div>

                    {/* Beschreibung */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Projektbeschreibung</label>
                            <span className="text-[11px] font-mono text-slate-400">{localDescription.length} / {DESCRIPTION_MAX}</span>
                        </div>
                        <textarea
                            value={localDescription}
                            onChange={e => setLocalDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
                            onBlur={commitDescription}
                            disabled={isReadOnly}
                            rows={5}
                            placeholder="Was wurde gebaut, für wen, was war das Besondere? 3–6 Sätze, so wie sie später im Beitrag stehen könnten."
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm leading-relaxed resize-y disabled:bg-slate-50 disabled:text-slate-500"
                        />
                    </div>

                    {/* Bilder */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Projektbilder (hochauflösend) · {items.length}</label>
                            <div className="flex items-center gap-2">
                                <button onClick={() => void reload()} disabled={loading} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-50" title="Neu laden">
                                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                </button>
                                {!isReadOnly && (
                                    <button onClick={() => fileInputRef.current?.click()} disabled={!canUpload} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
                                        {busy === 'upload' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                                        {uploadProgress ? `Lade ${uploadProgress.done + 1} / ${uploadProgress.total} …` : 'Bilder hinzufügen'}
                                    </button>
                                )}
                            </div>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            className="hidden"
                            onChange={e => handleFiles(e.target.files)}
                            disabled={!canUpload}
                        />

                        {tabelleFehlt && (
                            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{MEDIA_TABLE_MISSING_TEXT}</span>
                            </div>
                        )}

                        {items.length === 0 ? (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={!canUpload}
                                className="w-full min-h-[180px] rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-300 transition-colors flex flex-col items-center justify-center gap-2 text-slate-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                onDragOver={e => { e.preventDefault(); }}
                                onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                            >
                                {busy === 'upload' ? <Loader2 className="w-8 h-8 animate-spin text-indigo-500" /> : <ImagePlus className="w-8 h-8 text-indigo-500" />}
                                <span className="font-bold text-sm text-slate-700">{busy === 'upload' ? 'Wird hochgeladen …' : loading ? 'Lade …' : 'Bilder auswählen oder hierher ziehen'}</span>
                                <span className="text-xs">JPG, PNG oder WebP · bis {describeFileSize(REFERENCE_IMAGE_MAX_BYTES)} je Datei · mehrere auf einmal möglich</span>
                                {isReadOnly && <span className="text-[11px] text-amber-700">Nur-Ansicht: kein Upload möglich</span>}
                            </button>
                        ) : (
                            <div
                                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
                                onDragOver={e => { if (canUpload) e.preventDefault(); }}
                                onDrop={e => { if (canUpload) { e.preventDefault(); handleFiles(e.dataTransfer.files); } }}
                            >
                                {items.map((item, index) => {
                                    const verdict = resolutionVerdict(item.width, item.height);
                                    const itemBusy = busy === item.id;
                                    return (
                                        <div key={item.id} className={`rounded-xl border overflow-hidden bg-white flex flex-col ${item.titelbild ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'}`}>
                                            <a href={item.url} target="_blank" rel="noreferrer" className="block bg-slate-100 aspect-[4/3] relative" title="In voller Größe öffnen">
                                                <img src={item.url} alt={item.caption || item.fileName} className="w-full h-full object-cover" loading="lazy" />
                                                {item.titelbild && (
                                                    <span className="absolute top-2 left-2 text-[10px] font-bold bg-amber-400 text-amber-950 px-1.5 py-0.5 rounded shadow-sm flex items-center gap-1"><Star className="w-3 h-3 fill-current" /> Titelbild</span>
                                                )}
                                                <span className={`absolute bottom-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm ${verdict === 'gut' ? 'bg-emerald-600 text-white' : verdict === 'knapp' ? 'bg-amber-500 text-white' : verdict === 'klein' ? 'bg-rose-600 text-white' : 'bg-slate-600 text-white'}`} title={resolutionVerdictText(verdict)}>
                                                    {item.width && item.height ? `${item.width} × ${item.height}` : 'Maße unbekannt'} · {describeFileSize(item.sizeBytes)}
                                                </span>
                                            </a>
                                            <div className="p-3 space-y-2 flex-1 flex flex-col">
                                                <input
                                                    type="text"
                                                    value={captionDrafts[item.id] ?? ''}
                                                    onChange={e => setCaptionDrafts(d => ({ ...d, [item.id]: e.target.value.slice(0, CAPTION_MAX) }))}
                                                    onBlur={() => handleCaptionBlur(item)}
                                                    disabled={isReadOnly}
                                                    placeholder="Bildunterschrift (optional)"
                                                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-slate-50"
                                                />
                                                <div className="text-[10px] text-slate-400 truncate" title={item.fileName}>
                                                    {item.fileName}{item.createdAt ? ` · ${new Date(item.createdAt).toLocaleDateString('de-DE')}` : ''}{item.uploadedBy ? ` · ${item.uploadedBy}` : ''}
                                                </div>
                                                <div className="flex flex-wrap gap-1.5 pt-1 mt-auto">
                                                    <button onClick={() => handleDownload(item, index)} disabled={busy !== null} className="px-2 py-1 text-[11px] font-bold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50" title="Original herunterladen">
                                                        {itemBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Original
                                                    </button>
                                                    {!isReadOnly && !item.titelbild && (
                                                        <button onClick={() => handleTitelbild(item)} disabled={busy !== null} className="px-2 py-1 text-[11px] font-bold rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center gap-1 disabled:opacity-50" title="Als Titelbild fürs Referenzblatt">
                                                            <Star className="w-3 h-3" /> Titelbild
                                                        </button>
                                                    )}
                                                    {!isReadOnly && (
                                                        <button onClick={() => handleDelete(item)} disabled={busy !== null} className="px-2 py-1 text-[11px] font-bold rounded-md border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 flex items-center gap-1 disabled:opacity-50 ml-auto" title="Bild löschen">
                                                            <Trash2 className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {error && (
                            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2 whitespace-pre-line">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
                            </div>
                        )}
                        {notice && !error && (
                            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{notice}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
